import { HoldedChartAccountCombobox } from '@modules/Wallets/Wallet/components/interaction/HoldedChartAccountCombobox';
import { useHoldedChartOfAccounts } from '@modules/Wallets/Wallet/hooks/useManualTransaction';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { useToast } from '@shared/hooks/useToast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBilling } from '../../hooks/useUpdateBilling';

interface BillingData {
  invoice_wallet_compensation: boolean;
  wallet_visible: boolean;
  holded_id: string;
  holded_client_account: string;
  taxes: string[];
  billing_frequency: 'weekly' | 'monthly' | 'biweekly';
  cod_advance_days_after_delivery: number;
  invoice_expiration: number;
  minimum_monthly_fee: number | null;
  minimum_monthly_fee_grace_months: number | null;
}

interface EditBillingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: number;
  initialData: BillingData;
  onSuccess?: () => void;
}

export const EditBillingModal = ({ open, onOpenChange, teamId, initialData, onSuccess }: EditBillingModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { updateBillingAsync, isPending, reset } = useUpdateBilling(teamId);

  const [formData, setFormData] = useState<BillingData>(initialData);

  const {
    data: chartAccounts = [],
    isLoading: chartLoading,
    isError: chartError,
    error: chartErrorData,
  } = useHoldedChartOfAccounts(open);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (open) {
      setFormData(initialData);
    }
  }, [open, initialData]);

  const handleClose = () => {
    setFormData(initialData);
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    try {
      await updateBillingAsync({
        invoice_wallet_compensation: formData.invoice_wallet_compensation,
        wallet_visible: formData.wallet_visible,
        holded_id: formData.holded_id || null,
        holded_client_account: formData.holded_client_account || null,
        taxes: formData.taxes,
        billing_frequency: formData.billing_frequency,
        cod_advance_days_after_delivery: formData.cod_advance_days_after_delivery ?? 0,
        invoice_expiration: formData.invoice_expiration ?? 0,
        minimum_monthly_fee: formData.minimum_monthly_fee,
        minimum_monthly_fee_grace_months: formData.minimum_monthly_fee_grace_months,
      });

      toast({
        title: t('editBilling.success.title'),
        description: t('editBilling.success.description'),
      });

      onSuccess?.();
      handleClose();
    } catch {
      toast({
        title: t('editBilling.error.title'),
        description: t('editBilling.error.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('editBilling.modal.title')}</DialogTitle>
          <DialogDescription>{t('editBilling.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Frecuencia de facturación */}
          <div className="grid gap-2">
            <Label>
              {t('fields.billingFrequency')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.billing_frequency}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  billing_frequency: value as 'weekly' | 'monthly',
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('editBilling.placeholders.billingFrequency')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">{t('billingFrequencies.weekly')}</SelectItem>
                <SelectItem value="monthly">{t('billingFrequencies.monthly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Cuota mínima mensual */}
          <div className="grid gap-2">
            <Label>{t('fields.minimumMonthlyFee')}</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={formData.minimum_monthly_fee ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  minimum_monthly_fee: e.target.value === '' ? null : parseFloat(e.target.value) || 0,
                }))
              }
              placeholder={t('editBilling.placeholders.minimumMonthlyFee')}
            />
          </div>

          {/* Meses de carencia cuota mínima */}
          <div className="grid gap-2">
            <div className="flex items-center gap-1.5">
              <Label>{t('fields.minimumMonthlyFeeGraceMonths')}</Label>
              <InfoTooltip
                text={t('fields.minimumMonthlyFeeGraceMonthsTooltip')}
                dataTestId="minimum-monthly-fee-grace-months-tooltip"
                maxWidth="md"
              />
            </div>
            <Input
              type="number"
              min={0}
              step={1}
              value={formData.minimum_monthly_fee_grace_months ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  minimum_monthly_fee_grace_months: e.target.value === '' ? null : parseInt(e.target.value) || 0,
                }))
              }
              placeholder={t('editBilling.placeholders.minimumMonthlyFeeGraceMonths')}
            />
          </div>

          {/* Días vencimiento facturas */}
          <div className="grid gap-2">
            <Label>{t('fields.invoiceExpiration')}</Label>
            <Input
              type="number"
              min={0}
              value={formData.invoice_expiration}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  invoice_expiration: parseInt(e.target.value) || 0,
                }))
              }
              placeholder={t('editBilling.placeholders.invoiceExpiration')}
            />
          </div>

          {/* Días adelanto COD tras entrega */}
          <div className="grid gap-2">
            <Label>{t('fields.codAdvanceDaysAfterDelivery')}</Label>
            <Input
              type="number"
              min={0}
              value={formData.cod_advance_days_after_delivery}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  cod_advance_days_after_delivery: parseInt(e.target.value) || 0,
                }))
              }
              placeholder={t('editBilling.placeholders.codAdvanceDays')}
            />
          </div>

          {/* Impuestos */}
          <div className="grid gap-2">
            <Label>{t('fields.taxes')}</Label>
            <Select
              value={formData.taxes.length > 0 ? formData.taxes[0] : ''}
              onValueChange={(value) => {
                setFormData((prev) => ({
                  ...prev,
                  taxes: value ? [value] : [],
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('editBilling.placeholders.taxes')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="s_iva_21">{t('editBilling.taxes.options.s_iva_21')}</SelectItem>
                <SelectItem value="s_iva_nosujeto">{t('editBilling.taxes.options.s_iva_nosujeto')}</SelectItem>
                <SelectItem value="s_iva_intras">{t('editBilling.taxes.options.s_iva_intras')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('editBilling.taxes.help')}</p>
          </div>

          {/* Holded ID */}
          <div className="grid gap-2">
            <Label>
              {t('fields.holdedId')} <span className="text-destructive">*</span>
            </Label>
            <Input
              value={formData.holded_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, holded_id: e.target.value }))}
              placeholder={t('editBilling.placeholders.holdedId')}
            />
          </div>

          {chartError && (
            <Alert variant="destructive" className="border-destructive/50">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
              <AlertTitle>{t('editBilling.accountsLoadError.title')}</AlertTitle>
              <AlertDescription>
                {getErrorMessage(chartErrorData) || t('editBilling.accountsLoadError.description')}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label>
              {t('fields.holdedClientAccount')} <span className="text-destructive">*</span>
            </Label>
            {chartError ? (
              <Input
                value={formData.holded_client_account}
                onChange={(e) => setFormData((prev) => ({ ...prev, holded_client_account: e.target.value }))}
                placeholder={t('editBilling.placeholders.holdedClientAccount')}
                disabled={isPending}
                data-testid="edit-billing-holded-client-account-fallback"
              />
            ) : (
              <>
                <HoldedChartAccountCombobox
                  accounts={chartAccounts}
                  value={formData.holded_client_account}
                  storedValueField="num"
                  onValueChange={(code) => setFormData((prev) => ({ ...prev, holded_client_account: code }))}
                  disabled={isPending}
                  loading={chartLoading}
                  dataTestId="edit-billing-holded-client-account"
                />
                <p className="text-xs text-muted-foreground">{t('editBilling.holdedClientAccountHelp')}</p>
              </>
            )}
          </div>

          {/* Compensación wallet en factura */}
          <div className="flex items-center gap-3">
            <Switch
              id="invoice-wallet-compensation"
              checked={formData.invoice_wallet_compensation}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  invoice_wallet_compensation: checked,
                }))
              }
            />
            <Label htmlFor="invoice-wallet-compensation" className="cursor-pointer">
              {t('fields.invoiceWalletCompensation')}
            </Label>
          </div>

          {/* Wallet visible */}
          <div className="flex items-center gap-3">
            <Switch
              id="wallet-visible"
              checked={formData.wallet_visible}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  wallet_visible: checked,
                }))
              }
            />
            <Label htmlFor="wallet-visible" className="cursor-pointer">
              {t('fields.walletVisible')}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('editBilling.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isPending || !formData.holded_id || !formData.holded_client_account}
          >
            {isPending ? t('editBilling.buttons.saving') : t('editBilling.buttons.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
